import { httpClient } from "@coworker/http-client";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { INTERVAL } from "@coworker/app/src/constants/clientTime";

type AmountSettings = {
  pieces_per_multi_pack: string | null;
  pieces_per_pallet: string | null;
};

export async function fetchItemAmountSettings(
  itemNo: string,
  enablePalletMultiPack: boolean
) {
  const encodedItemNo = encodeURIComponent(itemNo);

  if (enablePalletMultiPack) {
    return await httpClient.data.get<unknown>(
      `/pallet-multipack/getPalletMultipack?itemNo=${encodedItemNo}`
    );
  }

  return await httpClient.tasks.get<unknown>(
    `/v1/shared/amount_settings?short_id=${encodedItemNo}`
  );
}

async function updateItemAmountSettings(
  itemNo: string,
  amounts: AmountSettings
) {
  const bodyData = {
    short_id: itemNo,
    pieces_per_multi_pack:
      amounts?.pieces_per_multi_pack !== "" &&
      amounts?.pieces_per_multi_pack !== null
        ? parseInt(amounts?.pieces_per_multi_pack)
        : null,
    pieces_per_pallet:
      amounts?.pieces_per_pallet !== "" && amounts?.pieces_per_pallet !== null
        ? parseInt(amounts?.pieces_per_pallet)
        : null,
  };
  return await httpClient.tasks.put<unknown>(
    `/v1/shared/amount_settings?short_id=${itemNo}`,
    { body: bodyData }
  );
}

export function useItemAmountSettings(
  itemNo: string,
  enablePalletMultiPack: boolean = false
) {
  return useQuery({
    queryKey: ["Item", "amountSettings", itemNo, enablePalletMultiPack],
    queryFn: () => fetchItemAmountSettings(itemNo, enablePalletMultiPack),
    staleTime: INTERVAL.DAY, // 1 day, considered fresh for a day.
    gcTime: INTERVAL.DAY * 7, // 7 days, garbage collect after a week.
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    enabled: !!itemNo && enablePalletMultiPack !== undefined,
  });
}

export const useUpdateItemAmountSettings = (itemNo: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (amounts: AmountSettings) =>
      updateItemAmountSettings(itemNo, amounts),
    onSuccess: () => {
      return queryClient.invalidateQueries({
        queryKey: ["Item", "amountSettings", itemNo],
      });
    },
  });
};
